import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import VpnLockIcon from '@mui/icons-material/VpnLock'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import Tooltip from '@mui/material/Tooltip'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'
import { DateTime } from 'luxon'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLayout from '../components/DashboardLayout'
import DashboardLoadingDataView from '../components/DashboardLoadingDataView'
import SsoConnectionFormFields from '@components/SsoConnectionFormFields'
import {
    attributeFieldsFromMap,
    attributeMapFromFields,
    buildSsoTestLoginUrl,
    defaultSsoFieldsValues,
    detectInputMode,
    HL_ACCOUNTS_UNCONFIGURED_MESSAGE,
    normalizeUsernameAttribute,
    parseEmailDomains,
    payloadForMode,
    resolveHlAccountsBaseUrl,
    ssoFieldsSchema,
    SsoFieldsValues,
} from '@components/SsoConnectionFormFields.helpers'
import useSession from '@hooks/useSession'
import { HASsoResult, SsoConnection } from '@types'

// Phase 2 — Step 4b.
//
// Edit page for an existing SSO connection. Pre-populates from
// `GET /api/ha/v2/sso-connections/:id`, saves via PUT, deletes via
// DELETE. `org_id` and `connection_id` are immutable post-create
// (matches habitat-admin's `updateBody = createBody.omit({ org_id,
// connection_id }).partial()`), so we render them as read-only chips
// rather than editable inputs.
//
// On save success: navigate to
// /manage-sso-connections?org=<orgId>&updated=<slug> — the list
// page reads `?updated=` and fires its own toast (same handoff
// pattern as ?created=).
// On delete success: navigate to
// /manage-sso-connections?org=<orgId>&deleted=<slug>.

type Props = { params: { id: string } }

const apiUrl = (id: string) => `/api/ha/v2/sso-connections/${id}`

export default function EditSsoConnectionPage({ params }: Props) {
    const session = useSession()!
    const [, setLocation] = useLocation()
    const id = params.id

    // The full row as it currently is in habitat-admin. We hold
    // this so the read-only header (org name lookup, connection_id,
    // created_at) has data, and so the dirty check can compare
    // against the loaded baseline rather than the form default.
    const [loaded, setLoaded] = useState<SsoConnection | null>(null)
    const [orgName, setOrgName] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [topError, setTopError] = useState<string | null>(null)

    const [form, setForm] = useState<SsoFieldsValues>(() =>
        defaultSsoFieldsValues()
    )
    const [errors, setErrors] = useState<
        Partial<Record<keyof SsoFieldsValues, string>>
    >({})
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [refreshing, setRefreshing] = useState(false)

    // Pre-populate the form from the existing row + best-effort org-
    // name lookup. Org-name fetch is best-effort — if it fails we
    // just show the UUID, so a habitat-admin hiccup on the orgs
    // endpoint doesn't block editing the connection.
    useEffect(() => {
        let cancelled = false

        const load = async () => {
            setLoading(true)
            setTopError(null)
            try {
                const response = await fetch(apiUrl(id), {
                    headers: {
                        Authorization: `Bearer ${session.accessToken}`,
                    },
                })
                const body =
                    (await response.json()) as HASsoResult<SsoConnection>
                if (cancelled) return

                if (!response.ok || body.error) {
                    setTopError(
                        body.error
                            ? body.message
                            : `Failed to load connection (HTTP ${response.status})`
                    )
                    setLoading(false)
                    return
                }

                setLoaded(body.result)
                setForm({
                    // Detect which input mode the loaded row is in
                    // (priority: metadata_xml > metadata_url > triple)
                    // so the tab control opens to the right tab and
                    // the matching field surfaces its existing value.
                    input_mode: detectInputMode(body.result),
                    idp_entity_id: body.result.idp_entity_id,
                    idp_sso_url: body.result.idp_sso_url,
                    idp_signing_cert: body.result.idp_signing_cert ?? '',
                    idp_metadata_url: body.result.idp_metadata_url ?? '',
                    idp_metadata_xml: body.result.idp_metadata_xml ?? '',
                    nameid_format: body.result.nameid_format,
                    username_attribute: body.result.username_attribute ?? '',
                    ...attributeFieldsFromMap(body.result.attribute_map),
                    email_domains_raw: (body.result.email_domains ?? []).join(
                        ', '
                    ),
                    allow_idp_initiated: body.result.allow_idp_initiated === 1,
                    enforce_sso: body.result.enforce_sso === 1,
                    enabled: body.result.enabled === 1,
                    require_response_signature:
                        body.result.require_response_signature === 1,
                })

                // Org-name lookup — failure is non-fatal.
                try {
                    const orgResp = await fetch(
                        `/api/ha/v2/orgs/id/${body.result.org_id}`,
                        {
                            headers: {
                                Authorization: `Bearer ${session.accessToken}`,
                            },
                        }
                    )
                    if (orgResp.ok) {
                        const orgBody = await orgResp.json()
                        const name =
                            orgBody?.result?.name ?? orgBody?.data?.name ?? null
                        if (!cancelled && name) setOrgName(name)
                    }
                } catch {
                    // swallow — UUID fallback is fine
                }
            } catch (err) {
                if (cancelled) return
                console.error(err)
                setTopError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to load connection'
                )
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()

        return () => {
            cancelled = true
        }
    }, [id, session.accessToken])

    // Single memoised parse feeds both the Save-button gate and the
    // per-field error effect below. Previously we called
    // `ssoFieldsSchema.safeParse(form)` twice per render — once in
    // the `formValid` memo, once in the errors-effect. The schema
    // includes a `superRefine` and (since the domain-shape audit fix)
    // a per-domain refine, so the parse cost is non-trivial; share
    // the result.
    const parsedForm = useMemo(() => ssoFieldsSchema.safeParse(form), [form])
    const formValid = parsedForm.success

    useEffect(() => {
        if (parsedForm.success) {
            setErrors({})
            return
        }
        const next: Partial<Record<keyof SsoFieldsValues, string>> = {}
        for (const issue of parsedForm.error.issues) {
            const field = issue.path[0] as keyof SsoFieldsValues
            if (!next[field]) next[field] = issue.message
        }
        setErrors(next)
    }, [parsedForm])

    // Mode-switch destructive-save detection. The 2026-05-01 re-audit
    // flagged `payloadForMode` as silently NULLing inactive-mode
    // columns: if the connection was loaded in `metadata_xml` mode
    // (with a populated XML column) and the operator clicks the
    // `triple` tab and Saves, the metadata XML is wiped server-side.
    // No confirmation, no warning — just a click+Save away from
    // permanent data loss.
    //
    // `columnsAboutToBeNulled` lists the loaded columns that would
    // be wiped if the current form were saved. Empty list = safe;
    // non-empty list = a warning chip renders inline AND the Save
    // handler routes through a confirmation dialog before
    // submitting. Visible signal first, gate second.
    const columnsAboutToBeNulled = useMemo<
        Array<'idp_signing_cert' | 'idp_metadata_url' | 'idp_metadata_xml'>
    >(() => {
        if (!loaded) return []
        const dropped: Array<
            'idp_signing_cert' | 'idp_metadata_url' | 'idp_metadata_xml'
        > = []
        const projected = payloadForMode(form)
        if (
            loaded.idp_signing_cert &&
            loaded.idp_signing_cert.length > 0 &&
            projected.idp_signing_cert === null
        ) {
            dropped.push('idp_signing_cert')
        }
        if (
            loaded.idp_metadata_url &&
            loaded.idp_metadata_url.length > 0 &&
            projected.idp_metadata_url === null
        ) {
            dropped.push('idp_metadata_url')
        }
        if (
            loaded.idp_metadata_xml &&
            loaded.idp_metadata_xml.length > 0 &&
            projected.idp_metadata_xml === null
        ) {
            dropped.push('idp_metadata_xml')
        }
        return dropped
    }, [loaded, form])

    // Confirmation dialog state for the destructive-save guard.
    // Opened from the Save handler when `columnsAboutToBeNulled` is
    // non-empty; the actual submission resumes in
    // `confirmDestructiveSave` after the operator OKs the dialog.
    const [destructiveSaveOpen, setDestructiveSaveOpen] = useState(false)

    // Dirty check compares the form to the loaded baseline (not to
    // a blank default), so a freshly-loaded record reads as
    // "clean" — Cancel won't ever ask "are you sure?" for an
    // un-edited row.
    const isDirty = useMemo(() => {
        if (!loaded) return false
        const baseline: SsoFieldsValues = {
            input_mode: detectInputMode(loaded),
            idp_entity_id: loaded.idp_entity_id,
            idp_sso_url: loaded.idp_sso_url,
            idp_signing_cert: loaded.idp_signing_cert ?? '',
            idp_metadata_url: loaded.idp_metadata_url ?? '',
            idp_metadata_xml: loaded.idp_metadata_xml ?? '',
            nameid_format: loaded.nameid_format,
            username_attribute: loaded.username_attribute ?? '',
            ...attributeFieldsFromMap(loaded.attribute_map),
            email_domains_raw: (loaded.email_domains ?? []).join(', '),
            allow_idp_initiated: loaded.allow_idp_initiated === 1,
            enforce_sso: loaded.enforce_sso === 1,
            enabled: loaded.enabled === 1,
            require_response_signature: loaded.require_response_signature === 1,
        }
        return Object.keys(form).some(
            (key) =>
                form[key as keyof SsoFieldsValues] !==
                baseline[key as keyof SsoFieldsValues]
        )
    }, [form, loaded])

    const setField = <K extends keyof SsoFieldsValues>(
        key: K,
        value: SsoFieldsValues[K]
    ) => setForm((prev) => ({ ...prev, [key]: value }))

    const cancelToList = () => {
        if (
            isDirty &&
            !window.confirm('Discard your changes? They will be lost.')
        ) {
            return
        }
        setLocation(
            loaded
                ? `/manage-sso-connections?org=${encodeURIComponent(loaded.org_id)}`
                : '/manage-sso-connections'
        )
    }

    /**
     * Save handler with destructive-save gate. The form's actual
     * Save button calls this; if the projected payload would NULL
     * one or more populated columns (mode-switch wipe), we open a
     * confirm dialog instead of submitting. The dialog's "Save
     * anyway" calls `submitSave()` directly to skip the gate the
     * second time around.
     */
    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formValid || saving || !loaded) return

        if (columnsAboutToBeNulled.length > 0) {
            setDestructiveSaveOpen(true)
            return
        }

        await submitSave()
    }

    const submitSave = async () => {
        if (!formValid || saving || !loaded) return

        setSaving(true)
        setTopError(null)

        const emailDomains = parseEmailDomains(form.email_domains_raw ?? '')

        // The mode-specific columns (`idp_signing_cert`,
        // `idp_metadata_url`, `idp_metadata_xml`) — `payloadForMode`
        // returns the active one and nulls the other two so a row
        // never carries stale data from a mode the staff swapped
        // away from.
        const modeFields = payloadForMode(form)

        // habitat-admin's updateBody is `.partial()` — we send the
        // full editable surface every time so a flag toggle on the
        // server resets to whatever the form says, not whatever was
        // there before. Avoids the "stale checkbox" drift surface.
        // `attribute_map` comes from the form's attribute-mapping fields,
        // pre-filled on load from the row (or the Shibboleth defaults).
        const payload = {
            idp_entity_id: form.idp_entity_id,
            idp_sso_url: form.idp_sso_url,
            idp_signing_cert: modeFields.idp_signing_cert,
            idp_metadata_url: modeFields.idp_metadata_url,
            idp_metadata_xml: modeFields.idp_metadata_xml,
            nameid_format: form.nameid_format,
            attribute_map: attributeMapFromFields(form),
            username_attribute: normalizeUsernameAttribute(
                form.username_attribute
            ),
            allow_idp_initiated: form.allow_idp_initiated,
            enforce_sso: form.enforce_sso,
            enabled: form.enabled,
            require_response_signature: form.require_response_signature,
            email_domains: emailDomains,
        }

        try {
            const response = await fetch(apiUrl(id), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.accessToken}`,
                },
                body: JSON.stringify(payload),
            })
            const body = (await response.json()) as HASsoResult<SsoConnection>
            if (!response.ok || body.error) {
                setTopError(
                    body.error
                        ? body.message
                        : `Failed to save changes (HTTP ${response.status})`
                )
                setSaving(false)
                return
            }
            setLocation(
                `/manage-sso-connections?org=${encodeURIComponent(
                    loaded.org_id
                )}&updated=${encodeURIComponent(loaded.connection_id)}`
            )
        } catch (err) {
            console.error(err)
            setTopError(
                err instanceof Error ? err.message : 'Failed to save changes'
            )
            setSaving(false)
        }
    }

    // Trigger an on-demand metadata refresh for this row. Calls the
    // same `ingestForConnection` path the cron uses on habitat-admin
    // — see `services/saml-metadata-ingester.ts`. On success, updates
    // the loaded row (so chips + error banner reflect reality) and,
    // *only if the form is clean*, resyncs the form fields too.
    // Refusing to clobber dirty form state preserves the staff
    // member's in-flight edits and matches the principle from the
    // dirty-form Cancel confirm: don't surprise them by silently
    // overwriting their work.
    const handleRefreshMetadata = async () => {
        if (!loaded || refreshing) return
        setRefreshing(true)
        try {
            const response = await fetch(
                `/api/ha/v2/sso-connections/${id}/refresh-metadata`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${session.accessToken}`,
                    },
                }
            )
            const body = (await response.json()) as
                | {
                      error: false
                      result: SsoConnection
                      // Optional: a success response without a `changed`
                      // summary should still count as a successful refresh
                      // (treated as "no changes"), not throw.
                      changed?: { ssoUrl: boolean; signingCert: boolean }
                  }
                | { error: true; message: string }

            if (!response.ok || body.error) {
                const message = body.error
                    ? body.message
                    : `HTTP ${response.status}`
                enqueueSnackbar(`Metadata refresh failed — ${message}`, {
                    variant: 'error',
                })
                // The server has already persisted the error to the
                // row's `last_metadata_error` column. Pull the row
                // back so the inline banner reflects it.
                try {
                    const stateResp = await fetch(apiUrl(id), {
                        headers: {
                            Authorization: `Bearer ${session.accessToken}`,
                        },
                    })
                    if (stateResp.ok) {
                        const stateBody = (await stateResp.json()) as
                            | { error: false; result: SsoConnection }
                            | { error: true; message: string }
                        if (!stateBody.error) {
                            setLoaded(stateBody.result)
                        }
                    }
                } catch {
                    // best-effort — the snackbar already told the
                    // staff what happened
                }
                return
            }

            const refreshed = body.result
            setLoaded(refreshed)
            if (!isDirty) {
                setForm({
                    input_mode: detectInputMode(refreshed),
                    idp_entity_id: refreshed.idp_entity_id,
                    idp_sso_url: refreshed.idp_sso_url,
                    idp_signing_cert: refreshed.idp_signing_cert ?? '',
                    idp_metadata_url: refreshed.idp_metadata_url ?? '',
                    idp_metadata_xml: refreshed.idp_metadata_xml ?? '',
                    nameid_format: refreshed.nameid_format,
                    username_attribute: refreshed.username_attribute ?? '',
                    ...attributeFieldsFromMap(refreshed.attribute_map),
                    email_domains_raw: (refreshed.email_domains ?? []).join(
                        ', '
                    ),
                    allow_idp_initiated: refreshed.allow_idp_initiated === 1,
                    enforce_sso: refreshed.enforce_sso === 1,
                    enabled: refreshed.enabled === 1,
                    require_response_signature:
                        refreshed.require_response_signature === 1,
                })
            }

            const changes: string[] = []
            if (body.changed?.ssoUrl) changes.push('SSO URL changed')
            if (body.changed?.signingCert) changes.push('signing cert rotated')
            const summary =
                changes.length === 0
                    ? 'no changes — already in sync'
                    : changes.join(', ')
            enqueueSnackbar(`Metadata refreshed (${summary}).`, {
                variant: 'success',
            })
        } catch (err) {
            console.error(err)
            enqueueSnackbar(
                `Metadata refresh failed — ${
                    err instanceof Error ? err.message : 'unknown error'
                }`,
                { variant: 'error' }
            )
        } finally {
            setRefreshing(false)
        }
    }

    const handleDelete = async () => {
        if (!loaded) return
        setDeleting(true)
        setTopError(null)
        try {
            const response = await fetch(apiUrl(id), {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${session.accessToken}`,
                },
            })
            // habitat-admin returns 204 on delete with no body — don't
            // try to parse JSON unless the response carries one.
            if (response.status === 204) {
                setLocation(
                    `/manage-sso-connections?org=${encodeURIComponent(
                        loaded.org_id
                    )}&deleted=${encodeURIComponent(loaded.connection_id)}`
                )
                return
            }
            const body = (await response
                .json()
                .catch(() => null)) as HASsoResult<unknown> | null
            if (!response.ok || (body && body.error)) {
                setTopError(
                    body && body.error
                        ? body.message
                        : `Failed to delete (HTTP ${response.status})`
                )
                setDeleting(false)
                setConfirmDelete(false)
                return
            }
            // Defensive — habitat-admin currently always returns 204.
            setLocation(
                `/manage-sso-connections?org=${encodeURIComponent(
                    loaded.org_id
                )}&deleted=${encodeURIComponent(loaded.connection_id)}`
            )
        } catch (err) {
            console.error(err)
            setTopError(
                err instanceof Error
                    ? err.message
                    : 'Failed to delete connection'
            )
            setDeleting(false)
            setConfirmDelete(false)
        }
    }

    // Only the *initial load* failure takes over the whole page —
    // there's no row to render a form for. Save / delete / refresh
    // errors (where `loaded` is non-null) surface as an inline Alert
    // inside the form below, so the operator's in-flight edits survive
    // a transient backend hiccup instead of being thrown away.
    if (topError && !loaded && !loading) {
        return <DashboardErrorView error={topError} loading={false} />
    }

    if (loading || !loaded) {
        return <DashboardLoadingDataView loading={loading} />
    }

    // Test-login is gated by two things: the connection must be
    // enabled (hl-accounts's lookup filters by `enabled=1` and would
    // 404 the test), and the hl-accounts base URL must be configured
    // for this build (otherwise `buildSsoTestLoginUrl` has nowhere to
    // point). Surface whichever reason applies in the tooltip rather
    // than handing the operator a dead button with no explanation.
    const testLoginBaseUrl = resolveHlAccountsBaseUrl()
    const testLoginDisabledReason =
        loaded.enabled !== 1
            ? 'Connection is currently disabled — flip the Enabled flag below and save before testing.'
            : !testLoginBaseUrl
              ? HL_ACCOUNTS_UNCONFIGURED_MESSAGE
              : null

    return (
        <DashboardLayout>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 4,
                    paddingBottom: 8,
                }}
            >
                <Paper sx={{ p: 4, width: '100%', maxWidth: 720 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ mb: 1 }}
                    >
                        <VpnLockIcon color="primary" />
                        <Typography variant="h6">
                            Edit SSO connection
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'gray', mb: 3 }}>
                        Organisation and connection ID are fixed at create time
                        — everything else can be edited here. Use the flag
                        toggles to enable / disable the connection or flip
                        enforce-SSO. Deletion is permanent and removes any
                        federated identities linked to this connection.
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={2}
                        flexWrap="wrap"
                        rowGap={1}
                        alignItems="center"
                        sx={{ mb: 3 }}
                    >
                        <Chip
                            label={`Org: ${orgName ?? loaded.org_id}`}
                            variant="outlined"
                        />
                        <Chip
                            label={`Connection ID: ${loaded.connection_id}`}
                            variant="outlined"
                        />
                        <Chip
                            label={`Provider: ${loaded.provider_type.toUpperCase()}`}
                            variant="outlined"
                        />
                        {(loaded.idp_metadata_url ||
                            loaded.idp_metadata_xml) && (
                            <Chip
                                label={
                                    loaded.last_metadata_refresh
                                        ? `Metadata refreshed ${DateTime.fromISO(
                                              loaded.last_metadata_refresh
                                          ).toRelative()}`
                                        : 'Metadata never refreshed'
                                }
                                variant="outlined"
                                color={
                                    loaded.last_metadata_error
                                        ? 'error'
                                        : loaded.last_metadata_refresh
                                          ? 'success'
                                          : 'default'
                                }
                            />
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        {/*
                          On-demand metadata refresh — calls the same
                          ingester path the cron uses on habitat-admin.
                          Only meaningful when the connection has a
                          metadata URL or stored XML to ingest from
                          (rows in `triple` mode have nothing to
                          refresh — the cert was typed in by hand).
                        */}
                        {(loaded.idp_metadata_url ||
                            loaded.idp_metadata_xml) && (
                            <Tooltip
                                title={
                                    loaded.idp_metadata_url
                                        ? 'Re-fetch the IdP metadata URL and update the cached SSO URL + signing cert. Useful after a cert rotation.'
                                        : 'Re-parse the stored metadata XML and update the cached SSO URL + signing cert.'
                                }
                            >
                                <span>
                                    <LoadingButton
                                        variant="outlined"
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        loading={refreshing}
                                        onClick={handleRefreshMetadata}
                                    >
                                        Refresh metadata
                                    </LoadingButton>
                                </span>
                            </Tooltip>
                        )}
                        {/*
                          Test-login button — exercises a real SAML
                          round-trip against this connection in a new
                          tab. Disabled when the connection is
                          `enabled=0` because hl-accounts's lookup
                          endpoint filters by `enabled=1` and would
                          404 the test, which would be a confusing
                          dead-end. We surface the reason in a tooltip
                          rather than silently hiding the button so
                          staff know the path exists.
                        */}
                        <Tooltip
                            title={
                                testLoginDisabledReason ??
                                'Opens a real SAML login round-trip in a new tab. Useful right after a cert rotation or IdP migration.'
                            }
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<OpenInNewIcon />}
                                    disabled={!!testLoginDisabledReason}
                                    onClick={() =>
                                        testLoginBaseUrl &&
                                        window.open(
                                            buildSsoTestLoginUrl(
                                                loaded.connection_id
                                            ),
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    }
                                >
                                    Test login
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>

                    {/*
                      Inline banner surfacing the last metadata-
                      refresh error from habitat-admin. Cleared when
                      the next refresh succeeds. Above the form so
                      it's the first thing staff see when they open
                      a connection that the cron has been failing on.
                    */}
                    {loaded.last_metadata_error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <AlertTitle>
                                Last metadata refresh failed
                            </AlertTitle>
                            {loaded.last_metadata_error}
                        </Alert>
                    )}

                    {/* Save / delete failures surface here (not as a
                        full-page takeover) so the operator's in-flight
                        edits survive a transient backend hiccup. */}
                    {topError && (
                        <Alert
                            severity="error"
                            onClose={() => setTopError(null)}
                            sx={{ mb: 3 }}
                        >
                            {topError}
                        </Alert>
                    )}

                    <form onSubmit={handleSave}>
                        <Stack spacing={3}>
                            <SsoConnectionFormFields
                                values={form}
                                setValue={setField}
                                errors={errors}
                            />

                            {columnsAboutToBeNulled.length > 0 && (
                                <Alert
                                    severity="warning"
                                    icon={<WarningAmberIcon />}
                                >
                                    <AlertTitle>
                                        Saving will clear stored data
                                    </AlertTitle>
                                    Switching from the loaded mode (
                                    <code>{detectInputMode(loaded)}</code>) to{' '}
                                    <code>{form.input_mode}</code> means Save
                                    will set{' '}
                                    {columnsAboutToBeNulled.map((col, i) => (
                                        <span key={col}>
                                            {i > 0 && ', '}
                                            <code>{col}</code>
                                        </span>
                                    ))}{' '}
                                    to NULL. The currently-stored value will be
                                    lost. You'll get a confirmation dialog
                                    before the write lands — but if this wasn't
                                    intentional, switch back to{' '}
                                    <code>{detectInputMode(loaded)}</code>{' '}
                                    before saving.
                                </Alert>
                            )}

                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ pt: 1 }}
                            >
                                <Button
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => setConfirmDelete(true)}
                                    disabled={saving || deleting}
                                >
                                    Delete connection
                                </Button>
                                <Stack direction="row" spacing={2}>
                                    <LoadingButton
                                        onClick={cancelToList}
                                        color="inherit"
                                        disabled={saving || deleting}
                                    >
                                        Cancel
                                    </LoadingButton>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        loading={saving}
                                        disabled={
                                            !formValid || !isDirty || saving
                                        }
                                        startIcon={<SaveIcon />}
                                    >
                                        Save changes
                                    </LoadingButton>
                                </Stack>
                            </Stack>
                        </Stack>
                    </form>
                </Paper>
            </Box>

            <Dialog
                open={confirmDelete}
                onClose={() => !deleting && setConfirmDelete(false)}
            >
                <DialogTitle>Delete this SSO connection?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This permanently removes the connection{' '}
                        <strong>{loaded.connection_id}</strong> for org{' '}
                        <strong>{orgName ?? loaded.org_id}</strong>. Any
                        federated identities created against it stay in
                        hl-accounts but lose their connection back to a live IdP
                        — re-creating the connection later under the same{' '}
                        <code>idp_entity_id</code> will reuse them.
                    </DialogContentText>
                    <Divider sx={{ my: 2 }} />
                    <DialogContentText>
                        This action cannot be undone via the UI.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDelete(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        loading={deleting}
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Destructive-save guard. Opened from `handleSave` when
                the projected payload would NULL one or more
                populated columns (mode-switch wipe). The dialog's
                "Save anyway" calls `submitSave()` directly — that
                bypasses `handleSave`'s gate the second time around,
                so confirmation isn't required twice. */}
            <Dialog
                open={destructiveSaveOpen}
                onClose={() => !saving && setDestructiveSaveOpen(false)}
            >
                <DialogTitle>Confirm destructive save</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        You're about to switch from the loaded mode (
                        <strong>{loaded && detectInputMode(loaded)}</strong>) to{' '}
                        <strong>{form.input_mode}</strong>. This will{' '}
                        <strong>permanently clear</strong> the currently-stored
                        value(s) for:
                    </DialogContentText>
                    <Stack
                        spacing={1}
                        sx={{
                            mb: 2,
                            pl: 2,
                            borderLeft: 3,
                            borderColor: 'warning.main',
                        }}
                    >
                        {columnsAboutToBeNulled.map((col) => (
                            <Typography
                                key={col}
                                variant="body2"
                                component="div"
                                sx={{ fontFamily: 'monospace' }}
                            >
                                <code>{col}</code>
                            </Typography>
                        ))}
                    </Stack>
                    <DialogContentText>
                        If you didn't mean to switch modes, click{' '}
                        <strong>Cancel</strong> and switch back to{' '}
                        <code>{loaded && detectInputMode(loaded)}</code> before
                        saving. If this is intentional (you're replacing the
                        metadata source), click <strong>Save anyway</strong>.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => setDestructiveSaveOpen(false)}
                        color="inherit"
                        disabled={saving}
                    >
                        Cancel
                    </LoadingButton>
                    <LoadingButton
                        onClick={async () => {
                            setDestructiveSaveOpen(false)
                            await submitSave()
                        }}
                        loading={saving}
                        color="warning"
                        variant="contained"
                        startIcon={<SaveIcon />}
                    >
                        Save anyway
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}
